import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Facebook, Instagram } from 'lucide-react';

export const Header = ({ onQuoteClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#home', label: 'Home' },
    { href: '#about', label: 'About' },
    { href: '#services', label: 'Services' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#reviews', label: 'Reviews' },
    { href: '#contact', label: 'Contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-neutral-950/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Top bar with contact info - RED themed */}
        <div className={`bg-red-600 text-white transition-all duration-300 ${isScrolled ? 'h-0 overflow-hidden' : 'h-auto'}`}>
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <motion.a 
                href="tel:7746967152" 
                className="flex items-center gap-2 hover:text-yellow-400 font-semibold transition-colors" 
                data-testid="phone-link-top"
                whileHover={{ scale: 1.05 }}
                aria-label="Call Rhino Seal at (774) 696-7152"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                (774) 696-7152
              </motion.a>
              <span className="hidden sm:block text-white/80">Worcester, MA</span>
            </div>
            <div className="flex items-center gap-4">
              <motion.a
                href="https://www.facebook.com/sealcoatingcentralmass/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-yellow-400 font-semibold transition-colors"
                data-testid="facebook-link-top"
                whileHover={{ scale: 1.05 }}
                aria-label="Follow Rhino Seal on Facebook"
              >
                <Facebook className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">1K Followers</span>
              </motion.a>
              <motion.a
                href="#"
                className="flex items-center gap-2 hover:text-yellow-400 font-semibold transition-colors"
                data-testid="instagram-link-top"
                whileHover={{ scale: 1.05 }}
                title="Instagram - Coming Soon"
                aria-label="Follow Rhino Seal on Instagram (Coming Soon)"
              >
                <Instagram className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">Instagram</span>
              </motion.a>
            </div>
          </div>
        </div>

        {/* Main navigation */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-24">
            {/* BIGGER Logo */}
            <motion.a 
              href="#home" 
              onClick={() => scrollToSection('#home')} 
              className="flex items-center gap-4" 
              data-testid="logo-link"
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <img
                src="/images/logo.jpg"
                alt="Rhino Seal Asphalt Maintenance"
                className="h-20 w-20 rounded-full object-cover border-4 border-red-600 shadow-lg shadow-red-600/30"
              />
              <div className="hidden sm:block">
                <span className="block text-2xl font-black text-white uppercase tracking-tight leading-tight">
                  Rhino Seal
                </span>
                <span className="block text-sm text-red-500 uppercase tracking-wider font-bold">
                  Asphalt Maintenance
                </span>
              </div>
            </motion.a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
              {navLinks.map((link) => (
                <motion.button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-white hover:text-red-500 font-semibold uppercase tracking-wider text-sm transition-colors relative"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  whileHover={{ scale: 1.1, y: -2 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                  aria-label={`Navigate to ${link.label} section`}
                >
                  {link.label}
                  <motion.div 
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-red-500"
                    initial={{ scaleX: 0 }}
                    whileHover={{ scaleX: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.button>
              ))}
            </nav>

            {/* CTA Button - RED themed */}
            <div className="flex items-center gap-4">
              <motion.button
                onClick={onQuoteClick}
                className="hidden sm:block bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-wider text-sm py-4 px-6 rounded-sm shadow-lg"
                data-testid="header-quote-btn"
                whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(220, 38, 38, 0.5)' }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                Get Free Quote
              </motion.button>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white hover:text-red-500 transition-colors"
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/80" onClick={() => setIsMobileMenuOpen(false)} />
            <motion.nav
              className="absolute top-24 right-0 w-72 bg-neutral-900 border-l-4 border-red-600 h-full"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
            >
              <div className="p-6 space-y-4">
                {/* Mobile logo */}
                <div className="flex items-center gap-3 pb-4 border-b border-red-600/30">
                  <img src="/images/logo.jpg" alt="Rhino Seal" className="w-12 h-12 rounded-full border-2 border-red-600" />
                  <span className="text-white font-black uppercase">Menu</span>
                </div>
                
                {navLinks.map((link) => (
                  <motion.button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className="block w-full text-left text-white hover:text-red-500 font-semibold uppercase tracking-wider py-3 border-b border-white/10 transition-colors"
                    whileHover={{ x: 10, color: '#ef4444' }}
                  >
                    {link.label}
                  </motion.button>
                ))}
                <motion.button
                  onClick={() => {
                    onQuoteClick();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-wider py-4 text-center mt-6 rounded-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Free Quote
                </motion.button>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky mobile call button - RED */}
      <motion.a
        href="tel:7746967152"
        className="fixed bottom-4 left-4 z-50 lg:hidden bg-red-600 text-white p-4 rounded-full shadow-lg"
        data-testid="mobile-call-btn"
        whileHover={{ scale: 1.1, boxShadow: '0 0 20px rgba(220, 38, 38, 0.6)' }}
        whileTap={{ scale: 0.9 }}
        aria-label="Call Rhino Seal at (774) 696-7152"
      >
        <Phone className="w-6 h-6" aria-hidden="true" />
      </motion.a>
    </>
  );
};

export default Header;
